import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/ui/multi-select";
import type { Project } from "@/services/project.service";
import type { McpPermission } from "@/services/mcp-connection.service";
import { ALL_PERMISSIONS, PERMISSION_GROUPS, READ_PERMISSIONS } from "./mcp-setup";

export function McpPermissionFields({
	permissions,
	onPermissionsChange,
	projectIds,
	onProjectIdsChange,
	projects,
}: {
	permissions: McpPermission[];
	onPermissionsChange: (p: McpPermission[]) => void;
	projectIds: string[];
	onProjectIdsChange: (ids: string[]) => void;
	projects: Project[];
}) {
	function toggle(p: McpPermission) {
		onPermissionsChange(
			permissions.includes(p) ? permissions.filter((x) => x !== p) : [...permissions, p],
		);
	}

	return (
		<>
			<div className="space-y-1.5">
				<div className="flex items-center justify-between">
					<label className="text-xs font-medium text-muted-foreground">Permissions</label>
					<div className="flex gap-1">
						<Button type="button" size="xs" variant="ghost" onClick={() => onPermissionsChange(READ_PERMISSIONS)}>
							Read only
						</Button>
						<Button type="button" size="xs" variant="ghost" onClick={() => onPermissionsChange(ALL_PERMISSIONS)}>
							Full access
						</Button>
					</div>
				</div>
				<div className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2">
					{PERMISSION_GROUPS.map((group) => (
						<div key={group.label} className="space-y-1.5">
							<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
								{group.label}
							</p>
							{group.items.map(({ key, label }) => (
								<div key={key} className="flex items-center gap-2">
									<Checkbox
										id={`mcp-perm-${key}`}
										checked={permissions.includes(key)}
										onCheckedChange={() => toggle(key)}
									/>
									<label htmlFor={`mcp-perm-${key}`} className="text-xs text-foreground">
										{label}
									</label>
								</div>
							))}
						</div>
					))}
				</div>
				<p className="text-xs text-muted-foreground">
					Your project role still applies. A connection can never do more than you can.
				</p>
			</div>

			<div className="space-y-1.5">
				<label className="text-xs font-medium text-muted-foreground">Projects</label>
				<MultiSelect
					value={projectIds}
					onValueChange={onProjectIdsChange}
					options={projects.map((p) => ({ value: p.id, label: p.name }))}
					placeholder="All my projects"
					size="sm"
					ariaLabel="Projects"
				/>
				<p className="text-xs text-muted-foreground">
					Leave empty to allow every project you are a member of, including future ones.
				</p>
			</div>
		</>
	);
}
